import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { ProductRow } from '../../../lib/supabase';

const ITEMS_PER_PAGE = 3;

export default function HotProducts() {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchFeatured = async () => {
      const { data } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .eq('is_featured', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      setProducts(data ?? []);
      setLoading(false);
    };
    fetchFeatured();
  }, []);

  if (loading || products.length === 0) return null;

  const hasArrows = products.length > ITEMS_PER_PAGE;
  const maxIndex = products.length - ITEMS_PER_PAGE;
  const visibleProducts = products.slice(currentIndex, currentIndex + ITEMS_PER_PAGE);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  return (
    <section className="w-full bg-white py-14 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Section Title */}
        <div className="flex items-center justify-center gap-4 mb-2">
          <span className="text-[#1a73b5] text-2xl leading-none">&#8942;</span>
          <h2 className="text-3xl font-bold text-gray-800 tracking-wider">熱門產品</h2>
          <span className="text-[#1a73b5] text-2xl leading-none">&#8942;</span>
        </div>
        <p className="text-center text-sm tracking-[0.3em] text-gray-400 uppercase mb-10">HOT PRODUCTS</p>

        {/* Divider */}
        <div className="flex items-center gap-0 mb-10">
          <div className="flex-1 h-px bg-[#1a73b5]" />
          <div className="w-2 h-2 bg-[#1a73b5] rotate-45 mx-1" />
          <div className="flex-1 h-px bg-[#1a73b5]" />
        </div>

        {/* Carousel Wrapper */}
        <div className="relative flex items-center gap-3">
          {/* Left Arrow */}
          {hasArrows && (
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:border-[#1a73b5] hover:text-[#1a73b5] transition-colors disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
              aria-label="上一個"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
          )}

          {/* Product Grid */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden">
            {visibleProducts.map((product) => (
              <a
                key={product.id}
                href={`/product/${product.category_id}/${product.sub_category_id || product.category_id}`}
                className="group block border border-gray-200 hover:border-[#1a73b5] transition-colors cursor-pointer overflow-hidden"
              >
                <div className="w-full h-52 flex items-center justify-center bg-white p-4 overflow-hidden">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.title}
                      title={product.title}
                      className="w-full h-full object-contain object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center">
                      <i className="ri-image-line text-5xl text-gray-100" />
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-100 px-4 py-3">
                  <h3 className="text-sm text-gray-700 text-center leading-snug">{product.title}</h3>
                  {product.model && (
                    <p className="text-xs font-mono text-[#1a73b5] text-center mt-1">{product.model}</p>
                  )}
                </div>
              </a>
            ))}
          </div>

          {/* Right Arrow */}
          {hasArrows && (
            <button
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:border-[#1a73b5] hover:text-[#1a73b5] transition-colors disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
              aria-label="下一個"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          )}
        </div>

        {/* Dots Indicator */}
        {hasArrows && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                  i === currentIndex ? 'bg-[#1a73b5]' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`第 ${i + 1} 頁`}
              />
            ))}
          </div>
        )}

        {/* More Button */}
        <div className="flex justify-center mt-10">
          <a
            href="/product"
            className="block w-0 h-0 border-l-[40px] border-r-[40px] border-b-[50px] border-l-transparent border-r-transparent border-b-[#1a73b5] relative cursor-pointer group"
            style={{ width: 0, height: 0 }}
          >
            <span className="sr-only">MORE</span>
          </a>
        </div>
        <div className="flex justify-center -mt-px">
          <a
            href="/product"
            className="inline-flex items-center justify-center bg-[#1a73b5] text-white text-sm font-bold tracking-widest px-10 py-2 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap"
          >
            MORE
          </a>
        </div>
      </div>
    </section>
  );
}
